import { ArrowRight, CheckCircle2, Star, Phone, Package, Clock, Shield, Euro, ChevronDown, Sofa, Box, Truck } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Contact from '../components/Contact';

export default function Teilumzug() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const scrollToContact = () => {
    const element = document.getElementById('kontakt');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const benefits = [
    'Nur zahlen, was wirklich transportiert wird',
    'Flexible Transportlösungen nach Mass',
    'Kombinierbar mit anderen Sendungen',
    'Fixpreis-Garantie ohne versteckte Kosten',
    'Professionelle Handhabung Ihrer Möbel',
    'Kostenlose Besichtigung und Beratung',
  ];

  const typicalItems = [
    { icon: Sofa, title: 'Einzelne Möbelstücke', description: 'Sofa, Schrank, Bett, Esstisch' },
    { icon: Box, title: 'Mehrere Kartons', description: 'Umzugskartons, Kisten, Gepäck' },
    { icon: Package, title: 'Spezielle Gegenstände', description: 'Klavier, Antiquitäten, Kunstwerke' },
  ];

  const priceExamples = [
    {
      title: 'Mini-Teilumzug',
      items: '1-5 Gegenstände',
      volume: 'bis 5m³',
      price: 'ab CHF 400.–',
      examples: 'z.B. Sofa + 3 Kartons',
    },
    {
      title: 'Standard-Teilumzug',
      items: '5-15 Gegenstände',
      volume: 'bis 15m³',
      price: 'ab CHF 900.–',
      examples: 'z.B. Schlafzimmer-Set + Kartons',
    },
    {
      title: 'Grosser Teilumzug',
      items: '15-30 Gegenstände',
      volume: 'bis 30m³',
      price: 'ab CHF 1\'800.–',
      examples: 'z.B. Wohnzimmer + Schlafzimmer',
    },
  ];

  const whyChooseUs = [
    {
      icon: Euro,
      title: 'Kostengünstig',
      description: 'Sie zahlen nur für den tatsächlich benötigten Platz. Durch Kombination mit anderen Transporten sparen Sie bis zu 60% gegenüber einem Komplettumzug. Ideal für Umzüge von Zürich Deutschland oder Basel Deutschland mit wenig Mobiliar.',
    },
    {
      icon: Clock,
      title: 'Flexibel',
      description: 'Wir richten uns nach Ihrem Zeitplan. Express-Transporte innerhalb 24-48 Stunden möglich oder günstigere Standardlieferung innerhalb 5-7 Tagen. Auch für Firmenumzüge mit wenig Mobiliar geeignet.',
    },
    {
      icon: Shield,
      title: 'Sicher',
      description: 'Vollständig versichert und professionell verpackt. Ihre Möbel und Gegenstände sind während des gesamten Transports geschützt.',
    },
    {
      icon: Truck,
      title: 'Professionell',
      description: 'Erfahrenes Team mit spezieller Ausbildung. Wir behandeln Ihre Möbel mit der gleichen Sorgfalt wie bei einem Komplettumzug.',
    },
  ];

  const testimonials = [
    {
      name: 'Martina S.',
      location: 'Zürich → München',
      text: 'Musste nur mein Sofa und ein paar Kartons transportieren. Der Teilumzug war perfekt und viel günstiger als ein kompletter LKW. Super Service!',
      rating: 5,
    },
    {
      name: 'Andreas B.',
      location: 'Basel → Stuttgart',
      text: 'Habe mein Klavier und einen Schrank transportieren lassen. Alles kam unbeschädigt an. Fixpreis wurde eingehalten. Sehr empfehlenswert!',
      rating: 5,
    },
    {
      name: 'Claudia M.',
      location: 'Bern → Frankfurt',
      text: 'Perfekt für meinen Studenten-Umzug. Nur ein paar Möbel und Kartons. Preis war fair und die Abwicklung unkompliziert. Danke!',
      rating: 5,
    },
  ];

  const faqs = [
    {
      question: 'Was ist ein Teilumzug und für wen eignet er sich?',
      answer: 'Ein <strong>Teilumzug</strong> bedeutet, dass nur ein Teil Ihres Hausrats transportiert wird – nicht eine komplette Wohnung. Ideal für <strong>Studenten</strong>, <strong>Senioren</strong>, <strong>Single-Haushalte</strong> oder wenn Sie nur einzelne Möbelstücke transportieren möchten. Auch perfekt für <strong>zweiten Wohnsitz</strong>, <strong>Ferienwohnung</strong> oder <strong>Nachsendungen</strong>.'
    },
    {
      question: 'Wie wird der Preis für einen Teilumzug berechnet?',
      answer: 'Der Preis richtet sich nach dem <strong>tatsächlich benötigten Volumen</strong> (in Kubikmetern), der <strong>Distanz</strong> und den gewünschten <strong>Zusatzleistungen</strong>. Sie zahlen nur für den Platz, den Ihre Gegenstände im LKW einnehmen. Bei der <strong>kostenlosen Besichtigung</strong> erstellen wir eine genaue Volumenberechnung und Sie erhalten einen <strong>transparenten Fixpreis</strong>.'
    },
    {
      question: 'Wird mein Teilumzug mit anderen Transporten kombiniert?',
      answer: 'Ja, bei der <strong>Standard-Option</strong> (5-7 Tage Lieferzeit) kombinieren wir mehrere Teilumzüge auf einer Route, um Kosten zu sparen. Ihre Möbel werden dabei separat markiert und sicher verstaut. Wenn Sie es eilig haben, bieten wir auch <strong>Express-Transporte</strong> an, bei denen Ihre Gegenstände direkt transportiert werden (24-48 Stunden).'
    },
    {
      question: 'Welche Gegenstände kann ich bei einem Teilumzug transportieren lassen?',
      answer: 'Praktisch alles: <strong>Möbel</strong> (Sofa, Schrank, Bett, Tisch), <strong>Kartons</strong>, <strong>Haushaltsgeräte</strong>, <strong>Fahrräder</strong>, <strong>Klaviere</strong>, <strong>Antiquitäten</strong> und mehr. Nicht erlaubt sind gefährliche Güter, verderbliche Lebensmittel und illegale Gegenstände. Bei speziellen oder wertvollen Gegenständen beraten wir Sie gerne zu optimalen Verpackung und Transport.'
    },
    {
      question: 'Wie lange dauert ein Teilumzug von der Schweiz nach Deutschland?',
      answer: '<strong>Express-Option:</strong> 24-48 Stunden Lieferzeit (Direkttransport)<br><strong>Standard-Option:</strong> 5-7 Tage Lieferzeit (kombinierter Transport)<br>Die genaue Dauer hängt von der Route und Verfügbarkeit ab. Bei der Buchung vereinbaren wir ein konkretes Abhol- und Lieferdatum.'
    },
    {
      question: 'Muss ich meine Möbel selbst verpacken?',
      answer: 'Nein, das müssen Sie nicht. Wir bieten einen <strong>kompletten Verpackungsservice</strong> an. Unser Team bringt professionelles <Link to="/verpackungsmaterial" className="text-slate-900 font-semibold hover:underline">Verpackungsmaterial</Link> mit und verpackt Ihre Möbel fachgerecht vor Ort. Alternativ können Sie auch selbst verpacken – wir stellen Ihnen auf Wunsch <strong>Umzugskartons und Verpackungsmaterial</strong> zur Verfügung.'
    },
    {
      question: 'Sind meine Gegenstände während des Teilumzugs versichert?',
      answer: 'Ja, alle Teilumzüge sind <strong>vollumfänglich versichert</strong>. Ihre Möbel und Gegenstände sind während des gesamten Transports gegen Schäden und Verlust abgesichert. Die <strong>Transportversicherung</strong> ist im Preis inbegriffen. Bei besonders wertvollen Gegenständen (über CHF 10\'000) können Sie eine zusätzliche Versicherung abschliessen.'
    },
    {
      question: 'Übernehmen Sie auch die Zollabwicklung für Teilumzüge nach Deutschland?',
      answer: 'Ja, selbstverständlich. Die <strong>komplette <Link to="/zollabwicklung" className="text-slate-900 font-semibold hover:underline">Zollabwicklung Schweiz Deutschland</Link></strong> ist auch bei Teilumzügen im Service inbegriffen. Wir erstellen alle erforderlichen Dokumente (Packliste, Zollerklärung) und kümmern uns um die Formalitäten am Grenzübergang – ohne Aufwand für Sie. Unsere Erfahrung bei <Link to="/umzug-zuerich-deutschland" className="text-slate-900 font-semibold hover:underline">Umzügen Zürich Deutschland</Link> garantiert reibungslose Abwicklung.'
    },
    {
      question: 'Kann ich meinen Teilumzug kurzfristig buchen?',
      answer: 'Ja, das ist möglich. Bei der <strong><Link to="/express-umzuege" className="text-slate-900 font-semibold hover:underline">Express-Option</Link></strong> können wir Ihren Teilumzug oft innerhalb von <strong>24-48 Stunden</strong> durchführen – je nach Verfügbarkeit und Route. Für die günstigere <strong>Standard-Option</strong> empfehlen wir eine Buchung <strong>1-2 Wochen im Voraus</strong>, damit wir Ihren Transport optimal planen können. Mehr zu unseren <Link to="/preise" className="text-slate-900 font-semibold hover:underline">Umzug Preisen</Link>.'
    },
    {
      question: 'Was ist der Unterschied zwischen Teilumzug und Komplettumzug?',
      answer: '<strong>Teilumzug:</strong> Nur ausgewählte Gegenstände werden transportiert. Günstiger, da nur genutzter Platz berechnet wird. Oft kombiniert mit anderen Transporten.<br><strong>Komplettumzug:</strong> Ihr gesamter Hausrat wird transportiert. Eigener LKW, meist schnellere Lieferung, umfassendere Services (Montage, Endreinigung etc.). Ideal für komplette Haushalte.'
    }
  ];

  const processSteps = [
    {
      step: '1',
      title: 'Anfrage & Beratung',
      description: 'Kontaktieren Sie uns per Telefon, E-Mail oder Formular. Wir besprechen Ihren Transportbedarf und vereinbaren eine kostenlose Besichtigung (vor Ort oder per Video).',
    },
    {
      step: '2',
      title: 'Besichtigung & Offerte',
      description: 'Wir begutachten die zu transportierenden Gegenstände, berechnen das Volumen und erstellen eine transparente Fixpreis-Offerte – ohne versteckte Kosten.',
    },
    {
      step: '3',
      title: 'Terminvereinbarung',
      description: 'Nach Ihrer Zusage vereinbaren wir Abhol- und Liefertermin. Sie wählen zwischen Express (24-48h) oder Standard (5-7 Tage) Option.',
    },
    {
      step: '4',
      title: 'Abholung',
      description: 'Unser Team holt Ihre Möbel und Gegenstände zum vereinbarten Termin ab. Auf Wunsch verpacken wir alles professionell vor Ort.',
    },
    {
      step: '5',
      title: 'Transport & Zoll',
      description: 'Ihre Gegenstände werden sicher transportiert. Wir übernehmen die komplette Zollabwicklung an der Grenze zur Schweiz-Deutschland.',
    },
    {
      step: '6',
      title: 'Lieferung',
      description: 'Pünktliche Lieferung an Ihre neue Adresse in Deutschland. Wir stellen alles an den gewünschten Platz und entsorgen das Verpackungsmaterial.',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <section className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white py-20 min-h-[700px] overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
            backgroundSize: '40px 40px'
          }} />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-semibold mb-6 border border-white/20">
              Teilumzug Schweiz → Deutschland
            </div>

            <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
              Teilumzug von der Schweiz nach Deutschland
            </h1>

            <p className="text-xl md:text-2xl text-slate-200 mb-8 leading-relaxed">
              Nur einzelne Möbel oder Kartons transportieren? Sparen Sie bis zu 60% mit unserem Teilumzug-Service
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <button
                onClick={scrollToContact}
                className="bg-white text-slate-900 px-8 py-4 rounded-lg font-semibold hover:bg-slate-100 transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 flex items-center justify-center space-x-2"
              >
                <span>Gratis Offerte erhalten</span>
                <ArrowRight className="w-5 h-5" />
              </button>
              <a
                href="tel:0800700100"
                className="bg-slate-700/50 backdrop-blur-sm text-white px-8 py-4 rounded-lg font-semibold hover:bg-slate-700 transition-all duration-300 border border-white/20 flex items-center justify-center space-x-2"
              >
                <Phone className="w-5 h-5" />
                <span>0800 700 100</span>
              </a>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-3xl mx-auto">
              {benefits.slice(0, 3).map((benefit, index) => (
                <div key={index} className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                  <CheckCircle2 className="w-6 h-6 text-green-400 mb-2 mx-auto" />
                  <p className="text-sm font-medium">{benefit}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
                Was ist ein Teilumzug?
              </h2>
              <p className="text-xl text-slate-600 leading-relaxed">
                Ein Teilumzug ist die ideale Lösung, wenn Sie nicht Ihren gesamten Hausrat, sondern nur einzelne Möbelstücke, Kartons oder spezielle Gegenstände von der Schweiz nach Deutschland transportieren möchten. Sie zahlen nur für den tatsächlich benötigten Platz im LKW – nicht für den ganzen Laderaum. Im Gegensatz zu unserem <Link to="/komplettumzug" className="text-slate-900 font-semibold hover:underline">Komplettumzug Schweiz Deutschland</Link> ist ein Teilumzug deutlich günstiger und flexibler.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6 mb-12">
              {typicalItems.map((item, index) => {
                const Icon = item.icon;
                return (
                  <div key={index} className="bg-slate-50 rounded-2xl p-6 text-center border-2 border-slate-100 hover:border-slate-300 transition-all duration-300">
                    <div className="bg-slate-800 w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-4">
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-slate-900 mb-2">{item.title}</h3>
                    <p className="text-slate-600">{item.description}</p>
                  </div>
                );
              })}
            </div>

            <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-8 border-2 border-green-200">
              <h3 className="text-2xl font-bold text-slate-900 mb-4 text-center">
                Perfekt für:
              </h3>
              <div className="grid md:grid-cols-2 gap-4">
                {[
                  'Studenten mit wenig Mobiliar',
                  'Senioren beim Wohnungswechsel',
                  'Single-Haushalte',
                  'Zweiter Wohnsitz einrichten',
                  'Nachsendungen nach Umzug',
                  'Einzelne Erbstücke transportieren',
                  'Möbel für Ferienwohnung',
                  'Arbeitszimmer-Ausstattung',
                ].map((item, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
                    <span className="text-slate-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
                Warum Teilumzug mit uns?
              </h2>
              <p className="text-xl text-slate-600">
                Die Vorteile auf einen Blick
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {whyChooseUs.map((item, index) => {
                const Icon = item.icon;
                return (
                  <div key={index} className="bg-white rounded-2xl p-8 border-2 border-slate-100 hover:border-slate-300 transition-all duration-300 hover:shadow-lg">
                    <div className="bg-slate-100 w-14 h-14 rounded-xl flex items-center justify-center mb-6">
                      <Icon className="w-7 h-7 text-slate-800" />
                    </div>
                    <h3 className="text-2xl font-bold text-slate-900 mb-3">{item.title}</h3>
                    <p className="text-slate-600 leading-relaxed">{item.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
                Preisbeispiele Teilumzug
              </h2>
              <p className="text-xl text-slate-600">
                Transparente Preise – Sie zahlen nur für genutzten Platz
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {priceExamples.map((example, index) => (
                <div key={index} className="bg-slate-50 rounded-2xl p-8 border-2 border-slate-200 hover:border-slate-800 transition-all duration-300">
                  <h3 className="text-2xl font-bold text-slate-900 mb-4">{example.title}</h3>
                  <div className="space-y-3 mb-6">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Gegenstände:</span>
                      <span className="font-semibold text-slate-900">{example.items}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Volumen:</span>
                      <span className="font-semibold text-slate-900">{example.volume}</span>
                    </div>
                  </div>
                  <div className="text-3xl font-bold text-slate-900 mb-4">{example.price}</div>
                  <div className="text-sm text-slate-600 italic">{example.examples}</div>
                  <div className="mt-6 pt-6 border-t border-slate-300">
                    <ul className="space-y-2 text-sm text-slate-600">
                      <li className="flex items-center space-x-2">
                        <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" />
                        <span>Zollabwicklung inklusive</span>
                      </li>
                      <li className="flex items-center space-x-2">
                        <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" />
                        <span>Transportversicherung</span>
                      </li>
                      <li className="flex items-center space-x-2">
                        <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" />
                        <span>Professionelle Handhabung</span>
                      </li>
                    </ul>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 text-center">
              <p className="text-slate-600 mb-4">
                * Preise sind Richtwerte für Standard-Transporte (5-7 Tage). Express-Option (24-48h) auf Anfrage.
              </p>
              <button
                onClick={scrollToContact}
                className="bg-slate-900 text-white px-8 py-3 rounded-lg font-semibold hover:bg-slate-800 transition-all duration-300 inline-flex items-center space-x-2"
              >
                <span>Jetzt individuelle Offerte einholen</span>
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
                So läuft Ihr Teilumzug ab
              </h2>
              <p className="text-xl text-slate-600">
                Von der Anfrage bis zur Lieferung – einfach und transparent
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {processSteps.map((step, index) => (
                <div key={index} className="bg-white rounded-2xl p-6 border-2 border-slate-100">
                  <div className="bg-slate-900 text-white w-12 h-12 rounded-xl flex items-center justify-center font-bold text-xl mb-4">
                    {step.step}
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3">{step.title}</h3>
                  <p className="text-slate-600 leading-relaxed">{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
                Das sagen unsere Kunden
              </h2>
              <p className="text-xl text-slate-600">
                Zufriedene Kunden bei über 1000 Teilumzügen pro Jahr
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {testimonials.map((testimonial, index) => (
                <div key={index} className="bg-slate-50 rounded-2xl p-6 border-2 border-slate-100">
                  <div className="flex items-center mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <p className="text-slate-700 mb-4 leading-relaxed">"{testimonial.text}"</p>
                  <div className="border-t border-slate-200 pt-4">
                    <div className="font-semibold text-slate-900">{testimonial.name}</div>
                    <div className="text-sm text-slate-500">{testimonial.location}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
                Häufig gestellte Fragen
              </h2>
              <p className="text-xl text-slate-600">
                Alles was Sie über Teilumzüge wissen müssen
              </p>
            </div>

            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div
                  key={index}
                  className="bg-white rounded-xl border-2 border-slate-100 overflow-hidden transition-all duration-300 hover:border-slate-300"
                >
                  <button
                    onClick={() => toggleFaq(index)}
                    className="w-full px-6 py-5 flex items-center justify-between text-left"
                  >
                    <span className="font-semibold text-slate-900 pr-4">{faq.question}</span>
                    <ChevronDown
                      className={`w-5 h-5 text-slate-600 transition-transform duration-300 flex-shrink-0 ${
                        openFaq === index ? 'transform rotate-180' : ''
                      }`}
                    />
                  </button>
                  {openFaq === index && (
                    <div className="px-6 pb-5">
                      <p className="text-slate-600 leading-relaxed" dangerouslySetInnerHTML={{ __html: faq.answer }} />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Contact />
    </div>
  );
}
